<template>
    <div class="position-relative">
        <div v-if="hasGuests" class="row g-3">
            <div
                v-for="guest in guests"
                :key="guest.id"
                class="col-12 col-sm-6 col-md-4 col-lg-3"
            >
                <div class="guest-card h-100">
                    <div class="status-accent"></div>
                    <div class="guest-card-body">
                        <div class="guest-head">
                            <div class="avatar">{{ initial(guest.guest_name) }}</div>
                            <div class="guest-head-text">
                                <div class="guest-name" :title="guest.guest_name">{{ guest.guest_name }}</div>
                                <div class="reservation-no">{{ guest.reservation_no }}</div>
                            </div>
                        </div>
                        <div class="rooms-line">
                            <i class="fa fa-bed me-2"></i>
                            <div v-if="guest.rooms && guest.rooms.length" class="room-chip-list">
                                <span v-for="room in guest.rooms" :key="room.name" class="room-chip" :class="{ 'room-chip-checked-out': room.status === 'checked_out' }">
                                    {{ room.name }}: {{ roomStatusLabel(room.status) }}
                                </span>
                            </div>
                            <span v-else class="rooms-text">{{ guest.rooms_text || '—' }}</span>
                        </div>
                        <span v-if="guest.room_status" class="room-status-badge" :class="roomStatusClass(guest.room_status)"><i class="fa fa-door-open me-1"></i>{{ roomStatusLabel(guest.room_status) }}</span>
                        <div v-if="guest.notes" class="notes-line" :title="guest.notes">
                            <i class="fa fa-sticky-note me-1"></i>{{ guest.notes }}
                        </div>
                        <div class="guest-card-footer">
                            <button
                                type="button"
                                class="btn btn-sm btn-success checkout-btn"
                                @click="onClickCheckout(guest)"
                            >
                                <i class="fa fa-sign-out-alt me-1"></i>Check Out
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="!hasGuests && !loading" class="empty-state">
            No pending checkout
        </div>

        <div class="mt-3">
            <Pagination :offset="offset" @onPageChange="onPageChange"/>
        </div>

        <CheckoutModal
            v-if="showCheckoutModal"
            :reservation-id="selectedGuestId"
            @onClose="showCheckoutModal = false"
            @onCheckedOut="onCheckedOut"
        />

        <Loader v-if="loading"/>
    </div>
</template>

<script setup>
import Loader from '@/components/atom/LoaderComponent';
import Pagination from '@/components/atom/Pagination';
import CheckoutModal from '@/components/molecule/company/hotel/hotel-frontdesk/CheckoutModal.vue';
import { computed, inject, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import handleHotel from '@/services/modules/hotel/hotel';

const $route = useRoute();
const $router = useRouter();
const $store = useStore();
const showError = inject('showError');
const { fetchHotelReservationList } = handleHotel();

const offset = ref(20);
const loading = ref(false);
const guests = ref([]);
const showCheckoutModal = ref(false);
const selectedGuestId = ref(null);

const hasGuests = computed(() => guests.value.length > 0);

const getQuery = () => {
    let query = `?company_id=${$route.params.companyId}`;
    query += '&reg_status=ready_for_checkout,on_claim';
    if ($route.query.page) query += '&page=' + $route.query.page;
    query += '&offset=' + offset.value;
    return query;
};

const setPagination = (data) => {
    if (!data.data.length) return;
    $store.commit('setPaginationData', {
        records: data.total,
        perPage: data.per_page,
        currentPage: data.current_page,
    });
};

const getPendingCheckoutList = async () => {
    loading.value = true;
    guests.value = [];
    await fetchHotelReservationList(getQuery()).then(res => {
        if (!res.status) {
            $store.commit('resetPagination');
            return;
        }
        guests.value = res.data.data;
        setPagination(res.data);
    }).catch(() => {
        showError('Something went wrong');
    }).finally(() => {
        loading.value = false;
    });
};

const onPageChange = async (page) => {
    const routeQuery = Object.assign({}, $route.query, { page });
    await $router.push({ path: $route.path, query: routeQuery });
    await getPendingCheckoutList();
};

const roomStatusLabels = {
    active: 'Occupied',
    checked_out: 'Checked Out',
    partially_checked_out: 'Partially Checked Out',
    fully_checked_out: 'Fully Checked Out',
};

const roomStatusLabel = (status) => {
    if (!status) return '—';
    if (!roomStatusLabels[status]) return status;
    return roomStatusLabels[status];
};

const roomStatusClasses = {
    active: 'room-status-active',
    partially_checked_out: 'room-status-partial',
    fully_checked_out: 'room-status-full',
};

const roomStatusClass = (status) => {
    if (!roomStatusClasses[status]) return 'room-status-active';
    return roomStatusClasses[status];
};

const initial = (name) => {
    if (!name) return '?';
    return name.charAt(0).toUpperCase();
};

const statusLabel = (status) => {
    if (status === 'ready_for_checkout') return 'Ready for Checkout';
    if (status === 'on_claim') return 'On Claim';
    return '—';
};

const statusClass = (status) => {
    if (status === 'on_claim') return 'status-on-claim';
    return 'status-ready';
};

const onClickCheckout = (guest) => {
    selectedGuestId.value = guest.id;
    showCheckoutModal.value = true;
};

const onCheckedOut = () => {
    showCheckoutModal.value = false;
    getPendingCheckoutList();
};

const reopenCheckoutModal = () => {
    const reopenId = $route.query.reopen_checkout;
    if (!reopenId) return;
    selectedGuestId.value = Number(reopenId);
    showCheckoutModal.value = true;
    const routeQuery = Object.assign({}, $route.query);
    delete routeQuery.reopen_checkout;
    $router.replace({ path: $route.path, query: routeQuery }).catch(() => {});
};

onMounted(() => {
    getPendingCheckoutList();
    reopenCheckoutModal();
});
</script>

<style scoped>
.guest-card {
    position: relative;
    display: flex;
    flex-direction: column;
    background: #ffffff;
    border: 1px solid #eceef1;
    border-radius: 14px;
    overflow: hidden;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
    transition: box-shadow 0.18s ease, transform 0.18s ease, border-color 0.18s ease;
}
.guest-card:hover {
    box-shadow: 0 10px 28px rgba(16, 24, 40, 0.14);
    transform: translateY(-3px);
    border-color: #dfe3e8;
}
.status-accent {
    height: 5px;
    background: linear-gradient(90deg, #1fa363, #37c98a);
}
.guest-card-body {
    display: flex;
    flex-direction: column;
    flex: 1;
    padding: 16px 18px 18px;
}
.guest-head {
    display: flex;
    align-items: center;
    gap: 12px;
}
.avatar {
    flex-shrink: 0;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: Georgia, 'Times New Roman', serif;
    font-size: 1.25rem;
    font-weight: 700;
    color: #1a7a49;
    background: #e6f6ee;
}
.guest-head-text {
    min-width: 0;
}
.status-badge {
    flex-shrink: 0;
    margin-left: auto;
    padding: 3px 9px;
    border-radius: 999px;
    font-size: 0.66rem;
    font-weight: 600;
    line-height: 1.4;
    white-space: nowrap;
}
.status-badge.status-ready {
    color: #1a7a49;
    background: #e6f6ee;
}
.status-badge.status-on-claim {
    color: #a3251c;
    background: #fdeaea;
}
.guest-name {
    font-size: 1.02rem;
    font-weight: 700;
    color: #101828;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.reservation-no {
    margin-top: 2px;
    font-size: 0.75rem;
    letter-spacing: 0.03em;
    color: #98a2b3;
}
.rooms-line {
    display: flex;
    align-items: center;
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px dashed #eceef1;
    font-size: 0.9rem;
    color: #475467;
}
.rooms-line .fa-bed {
    color: #98a2b3;
}
.rooms-text {
    font-weight: 600;
    white-space: normal;
    word-break: break-word;
}
.room-chip-list {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}
.room-chip {
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 0.7rem;
    font-weight: 600;
    color: #475467;
    background: #f2f4f7;
}
.room-chip-checked-out {
    color: #98a2b3;
    background: #eceef1;
    text-decoration: line-through;
}
.room-status-badge {
    align-self: flex-start;
    margin-top: 10px;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 0.66rem;
    font-weight: 600;
}
.room-status-active {
    color: #1c6b3a;
    background: #c6e9d3;
}
.room-status-partial {
    color: #8a5a00;
    background: #f7e2b3;
}
.room-status-full {
    color: #475467;
    background: #f2f4f7;
}
.notes-line {
    display: block;
    margin-top: 8px;
    font-size: 0.76rem;
    color: #667085;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.notes-line .fa-sticky-note {
    color: #98a2b3;
}
.guest-card-footer {
    margin-top: auto;
    padding-top: 14px;
}
.empty-state {
    text-align: center;
    color: #98a2b3;
    padding: 60px 0;
    font-size: 0.95rem;
}
.empty-state .fa-sign-out-alt {
    font-size: 2rem;
    opacity: 0.5;
}
</style>
